import type {
  CompletionStatus,
  CrossFlowContextType,
  Options,
} from '@atlassiansox/cross-flow-support';
import type { AdTouchpointSourceType } from '@trello/ad-sequencer';
import { isDesktop } from '@trello/browser';
import type { FeatureExperimentKeys } from '@trello/feature-gate-client';

import { isCrossFlowIFrameOpenState } from './experimental/isCrossFlowIFrameOpenState';
import type { FeatureFlagsTouchpoints } from './experimental/isSourceTargetedByFeatureFlag';
import type { EligibleWorkspaceOptions } from './experimental/useEligibleWorkspacesForProvisioning';

export interface TrelloCrossFlow<T> {
  open: (options?: Options) => Promise<CompletionStatus | undefined>;
  /**
   * getIsTargeted returns whether the user is targeted, but does not necessarily mean anything about eligibility.
   * Targeted users may then be checked under more criteria and possibly allocated to a not enrolled cohort.
   */
  getIsTargeted: () => boolean;
  /**
   * Returns the experiment cohort after all runtime criteria have been checked.
   * May return null or the not enrolled variation if the user is not enrolled in the experiment.
   * Emits an exposure event for the user and their cohort if the user is enrolled in the experiment.
   */
  getExperimentCohort: () => T | null;

  /**
   * Returns the name of the experiment being used.
   * May return undefined if the user is not enrolled in any experiment(s).
   */
  experimentName?: FeatureExperimentKeys | FeatureFlagsTouchpoints;
}

export interface TrelloCrossFlowExperiment<T> extends TrelloCrossFlow<T> {
  /**
   * Returns whether the user is enrolled in the experiment.
   */
  isEnrolled: boolean;
}

export const trelloCrossFlowOpen = (
  crossflow: CrossFlowContextType,
  source: AdTouchpointSourceType,
  eligibleWorkspaceOptions: EligibleWorkspaceOptions,
  hasValidAaSession: boolean,
  {
    workspaceId,
    boardId,
    isNewTrelloUser,
    sporkPositioning,
  }: {
    workspaceId?: string;
    boardId?: string;
    isNewTrelloUser?: boolean;
    sporkPositioning?: 'collaboration' | 'customization';
  } = {},
) => {
  return async () => {
    // Redirect to login if AA session is invalid
    if (!hasValidAaSession) {
      window.location.assign(`/login`);
      return;
    }

    // Disable crossFlow on desktop due to compliance risks
    if (isDesktop()) {
      return;
    }

    if (!crossflow.isEnabled) {
      return;
    }

    isCrossFlowIFrameOpenState.setValue(true);

    const targetProduct = sporkPositioning
      ? 'jira-software.ondemand'
      : 'jira-core.ondemand';

    const response = await crossflow.api.open({
      targetProduct,
      journey: 'get-started',
      sourceComponent: source,
      sourceContext: 'get-started',
      experimentalOptions: {
        trelloWorkspaceId: workspaceId || null,
        trelloWorkspaceOptions: eligibleWorkspaceOptions,
        trelloBoardId: boardId || null,
        isNewTrelloUser: isNewTrelloUser || false,
        ...(sporkPositioning ? { sporkPositioning } : {}),
      },
    });

    isCrossFlowIFrameOpenState.setValue(false);
    return response;
  };
};
